import { useQuery } from "@tanstack/react-query";
import { useRoute, Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowLeft,
  Send,
  Building2,
  Calendar,
  Mail,
  Shield,
  Heart,
  Eye,
  Stethoscope,
  Clock,
  Users,
  FileText,
  Briefcase,
  Activity,
} from "lucide-react";
import { GaMark } from "@/lib/quoting/GaMark";
import { coverageItems } from "@shared/utils/rfpEmailContent";
import type { Quote } from "@shared/schema";

interface RFPSentData {
  sentAt: string;
  recipients: Array<{
    name: string;
    email: string;
    carrierName: string;
    coverageTypes: string[];
    /** Both optional: RFPs sent before the send recorded them have neither. */
    generalAgencyId?: number | null;
    /** GA mode: what this firm was asked for, as it stood on the day. */
    carrierNames?: string[];
  }>;
  cc?: string[];
}

interface WizardState {
  mode?: string;
  clientType?: string;
  selectedEntityId?: number;
  effectiveDate?: string;
  coverageTypes?: string[];
  rfpParameters?: Record<string, any>;
  rfpSentData?: RFPSentData;
}

interface QuoteWithWizardState extends Omit<Quote, 'wizardState'> {
  wizardState?: WizardState | null;
}

const formatDate = (dateInput: string | Date | null) => {
  if (!dateInput) return "N/A";
  const d = typeof dateInput === "string" ? new Date(dateInput) : dateInput;
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const formatDateTime = (dateInput: string | Date | null) => {
  if (!dateInput) return "N/A";
  const d = typeof dateInput === "string" ? new Date(dateInput) : dateInput;
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getCoverageIcon = (type: string) => {
  switch (type?.toLowerCase()) {
    case "health":
    case "medical":
      return Heart;
    case "dental":
      return Stethoscope;
    case "vision":
      return Eye;
    case "life":
      return Shield;
    case "disability":
      return Activity;
    case "voluntary":
      return Briefcase;
    default:
      return Shield;
  }
};

const getCoverageColor = (type: string) => {
  switch (type?.toLowerCase()) {
    case "health":
    case "medical":
      return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300";
    case "dental":
      return "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300";
    case "vision":
      return "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300";
    case "life":
      return "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300";
    case "disability":
      return "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300";
    case "voluntary":
      return "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300";
    default:
      return "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300";
  }
};

const formatCoverageLabel = (type: string) => {
  const labels: Record<string, string> = {
    dental: "Dental",
    vision: "Vision",
    life: "Life Insurance",
    disability: "Disability",
    voluntary: "Voluntary Benefits",
    health: "Health",
    medical: "Medical",
  };
  return labels[type?.toLowerCase()] || type;
};

const formatNetworkType = (type: string) => {
  const types: Record<string, string> = {
    dppo: "DPPO",
    dhmo: "DHMO",
    ppo: "PPO",
    hmo: "HMO",
    epo: "EPO",
  };
  return types[type?.toLowerCase()] || type || "N/A";
};

const formatEliminationPeriod = (period: string) => {
  const periods: Record<string, string> = {
    "0_days": "0 Days",
    "7_days": "7 Days",
    "14_days": "14 Days",
    "30_days": "30 Days",
    "60_days": "60 Days",
    "90_days": "90 Days",
    "180_days": "180 Days",
  };
  return periods[period] || period || "N/A";
};

const formatBenefitDuration = (duration: string) => {
  const durations: Record<string, string> = {
    "13_weeks": "13 Weeks",
    "26_weeks": "26 Weeks",
    "52_weeks": "52 Weeks",
    "ssnra": "To Social Security Normal Retirement Age",
    "age_65": "To Age 65",
    "5_years": "5 Years",
    "2_years": "2 Years",
  };
  return durations[duration] || duration || "N/A";
};

const formatDefinitionOfDisability = (def: string) => {
  const definitions: Record<string, string> = {
    "own_occ_12": "Own Occupation (12 months)",
    "own_occ_24": "Own Occupation (24 months)",
    "any_occ": "Any Occupation",
    "modified_any": "Modified Any Occupation",
  };
  return definitions[def] || def || "N/A";
};

function RFPParametersDisplay({ type, params }: { type: string; params: any }) {
  if (!params) return null;

  const renderDentalParams = () => (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <p className="text-sm text-muted-foreground">Network Type</p>
        <p className="font-medium">{formatNetworkType(params.networkType)}</p>
      </div>
      <div>
        <p className="text-sm text-muted-foreground">Annual Maximum Benefit</p>
        <p className="font-medium">${params.maximumBenefit?.toLocaleString() || params.customMaximum?.toLocaleString() || "N/A"}</p>
      </div>
      <div>
        <p className="text-sm text-muted-foreground">Perio/Endo Coverage</p>
        <p className="font-medium capitalize">{params.perioEndoCoverage || "N/A"}</p>
      </div>
      <div>
        <p className="text-sm text-muted-foreground">Orthodontic Coverage</p>
        <p className="font-medium capitalize">{params.orthodonticCoverage?.replace(/_/g, " ") || "None"}</p>
      </div>
      {params.orthodonticCoverage && params.orthodonticCoverage !== "none" && (
        <div>
          <p className="text-sm text-muted-foreground">Orthodontic Maximum</p>
          <p className="font-medium">${params.orthodonticMaximum?.toLocaleString() || params.customOrthoMaximum?.toLocaleString() || "N/A"}</p>
        </div>
      )}
      <div>
        <p className="text-sm text-muted-foreground">Coverage Types</p>
        <p className="font-medium capitalize">{params.coverageTypes?.join(", ").replace(/_/g, " ") || "N/A"}</p>
      </div>
    </div>
  );

  const renderVisionParams = () => (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <p className="text-sm text-muted-foreground">Materials Copay</p>
        <p className="font-medium">{params.materialsCopay === "included" ? "Included" : `$${params.materialsCopay || params.customCopayAmount || "N/A"}`}</p>
      </div>
      <div>
        <p className="text-sm text-muted-foreground">Frame Allowance</p>
        <p className="font-medium">${params.allowanceAmount?.toLocaleString() || "N/A"}</p>
      </div>
      <div>
        <p className="text-sm text-muted-foreground">Benefits Frequency</p>
        <p className="font-medium capitalize">{params.benefitsFrequency || "N/A"}</p>
      </div>
      <div>
        <p className="text-sm text-muted-foreground">Coverage Types</p>
        <p className="font-medium capitalize">{params.coverageTypes?.join(", ").replace(/_/g, " ") || "N/A"}</p>
      </div>
    </div>
  );

  const renderLifeParams = () => {
    const coverageAmounts = params.coverageAmounts || [];
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-muted-foreground">Coverage Types</p>
            <p className="font-medium capitalize">{params.coverageTypes?.join(", ").replace(/_/g, " ") || "N/A"}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Voluntary Buy-Up</p>
            <p className="font-medium">{params.voluntaryBuyUp ? "Yes" : "No"}</p>
          </div>
        </div>
        {coverageAmounts.length > 0 && (
          <div>
            <p className="text-sm text-muted-foreground mb-2">Coverage Amounts</p>
            <div className="space-y-2">
              {coverageAmounts.map((amt: any, idx: number) => (
                <div key={idx} className="bg-muted/50 rounded p-3">
                  {amt.type === "flat_amount" ? (
                    <p className="font-medium">Flat Amount: ${amt.flatAmount?.toLocaleString()}</p>
                  ) : (
                    <p className="font-medium">
                      {amt.multipleOfSalary}x Salary (Max: ${amt.maximumAmount?.toLocaleString()})
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderDisabilityParams = () => (
    <div className="space-y-6">
      {params.stdParams && (
        <div>
          <h4 className="font-medium mb-3">Short-Term Disability (STD)</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Benefit Percentage</p>
              <p className="font-medium">{params.stdParams.benefitPercentage}%</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Maximum Weekly Benefit</p>
              <p className="font-medium">${params.stdParams.maximumWeeklyBenefit || params.stdParams.customMaxWeeklyBenefit?.toLocaleString() || "N/A"}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Elimination Period</p>
              <p className="font-medium">{formatEliminationPeriod(params.stdParams.eliminationPeriod)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Benefit Duration</p>
              <p className="font-medium">{formatBenefitDuration(params.stdParams.benefitDuration)}</p>
            </div>
          </div>
        </div>
      )}
      {params.ltdParams && (
        <div>
          <h4 className="font-medium mb-3">Long-Term Disability (LTD)</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Benefit Percentage</p>
              <p className="font-medium">{params.ltdParams.benefitPercentage}%</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Maximum Monthly Benefit</p>
              <p className="font-medium">${params.ltdParams.maximumMonthlyBenefit || params.ltdParams.customMaxMonthlyBenefit?.toLocaleString() || "N/A"}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Elimination Period</p>
              <p className="font-medium">{formatEliminationPeriod(params.ltdParams.eliminationPeriod)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Benefit Duration</p>
              <p className="font-medium">{formatBenefitDuration(params.ltdParams.benefitDuration)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Definition of Disability</p>
              <p className="font-medium">{formatDefinitionOfDisability(params.ltdParams.definitionOfDisability)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Covered Earnings</p>
              <p className="font-medium capitalize">{params.ltdParams.coveredEarningsDefinition?.replace(/_/g, " ") || "N/A"}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderVoluntaryParams = () => (
    <div className="space-y-4">
      <div>
        <p className="text-sm text-muted-foreground">Selected Benefits</p>
        <div className="flex flex-wrap gap-2 mt-2">
          {params.selectedBenefits?.length > 0 ? params.selectedBenefits.map((benefit: string) => (
            <Badge key={benefit} variant="secondary">
              {benefit.replace(/_/g, " ").replace(/\b\w/g, (l: string) => l.toUpperCase())}
            </Badge>
          )) : <span className="text-muted-foreground">None selected</span>}
        </div>
      </div>
    </div>
  );

  // Rows straight from the email builder, so the page shows exactly what the
  // carrier was sent. Medical uses it; the five renderers above predate it and
  // are left alone rather than rewritten under a bug fix.
  const renderFromEmailBuilder = () => {
    const items = coverageItems(type, params);
    if (!items) {
      return (
        <p className="text-sm text-muted-foreground">
          No requirements were specified for this coverage.
        </p>
      );
    }
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {items.map((item) => (
          <div key={item.label}>
            <p className="text-sm text-muted-foreground">{item.label}</p>
            <p className="font-medium">{item.value}</p>
          </div>
        ))}
      </div>
    );
  };

  switch (type?.toLowerCase()) {
    case "dental":
      return renderDentalParams();
    case "vision":
      return renderVisionParams();
    case "life":
      return renderLifeParams();
    case "disability":
      return renderDisabilityParams();
    case "voluntary":
      return renderVoluntaryParams();
    // Medical, and anything else that gains a form later: no hand-written
    // renderer, and a raw JSON dump is not an answer.
    default:
      return renderFromEmailBuilder();
  }
}

export function RFPDetails() {
  const [, params] = useRoute("/quoting/rfp/:id");
  const quoteId = params?.id ? parseInt(params.id) : null;

  const { data: quote, isLoading, error } = useQuery<QuoteWithWizardState>({
    queryKey: ["/api/quotes", quoteId],
    queryFn: async () => {
      const response = await fetch(`/api/quotes/${quoteId}`);
      if (!response.ok) throw new Error("Failed to fetch RFP details");
      return response.json();
    },
    enabled: !!quoteId,
  });

  if (isLoading) {
    return (
      <div className="container mx-auto py-8 px-4 max-w-6xl">
        <Skeleton className="h-8 w-48 mb-6" />
        <div className="grid gap-6">
          <Skeleton className="h-48" />
          <Skeleton className="h-64" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="container mx-auto py-8 px-4 max-w-6xl">
        <Card>
          <CardContent className="p-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">RFP Not Found</h3>
            <p className="text-muted-foreground mb-4">
              The requested RFP could not be found.
            </p>
            <Button asChild>
              <Link href="/quoting">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Quotes
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const wizardState = quote.wizardState as WizardState | undefined;
  const rfpSentData = wizardState?.rfpSentData;
  const coverageTypes = wizardState?.coverageTypes || quote.coverageTypes || [];
  const rfpParameters = wizardState?.rfpParameters || {};
  const effectiveDate = wizardState?.effectiveDate || quote.effectiveDate;
  const selectedCarriers = (wizardState as any)?.selectedCarriers || {};

  const uniqueCarriers = rfpSentData?.recipients
    ? Array.from(new Set(rfpSentData.recipients.map(r => r.carrierName)))
    : [];
  
  const carrierCount = quote.carrierIds?.length || Object.keys(selectedCarriers).length || 0;

  return (
    <div className="container mx-auto py-8 px-4 max-w-6xl">
      <div className="mb-6">
        <Button variant="ghost" asChild className="mb-4">
          <Link href="/quoting">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Quotes
          </Link>
        </Button>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">{quote.companyName}</h1>
            <p className="text-muted-foreground mt-1">Request for Proposal Details</p>
          </div>
          <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
            <Send className="h-3 w-3 mr-1" />
            RFP Sent
          </Badge>
        </div>
      </div>

      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              RFP Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div>
                <p className="text-sm text-muted-foreground">Company</p>
                <p className="font-medium">{quote.companyName}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Employees</p>
                <p className="font-medium">{quote.employeeCount}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Effective Date</p>
                <p className="font-medium">{formatDate(effectiveDate)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Sent Date</p>
                <p className="font-medium">
                  {rfpSentData?.sentAt ? formatDateTime(rfpSentData.sentAt) : formatDate(quote.updatedAt)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Coverage Lines Requested
            </CardTitle>
            <CardDescription>
              {coverageTypes.length} coverage type{coverageTypes.length !== 1 ? "s" : ""} included in this RFP
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {coverageTypes.map((type: string) => {
                const Icon = getCoverageIcon(type);
                return (
                  <Badge
                    key={type}
                    variant="secondary"
                    className={`${getCoverageColor(type)} px-4 py-2 text-sm`}
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    {formatCoverageLabel(type)}
                  </Badge>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Recipients
            </CardTitle>
            <CardDescription>
              {rfpSentData?.recipients?.length 
                ? `${rfpSentData.recipients.length} recipient${rfpSentData.recipients.length !== 1 ? "s" : ""} across ${uniqueCarriers.length} carrier${uniqueCarriers.length !== 1 ? "s" : ""}`
                : `Sent to ${carrierCount} carrier${carrierCount !== 1 ? "s" : ""}`
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {rfpSentData?.recipients && rfpSentData.recipients.length > 0 ? (
              <div className="space-y-4">
                {uniqueCarriers.map(carrierName => {
                  const carrierRecipients = rfpSentData.recipients.filter(r => r.carrierName === carrierName);
                  // Through a GA, carrierName IS the firm's name, so this is
                  // already grouped by firm. The mark makes it look like the
                  // firm it is rather than like a carrier.
                  const firmId = carrierRecipients.find((r) => r.generalAgencyId)?.generalAgencyId;
                  const askedFor = Array.from(
                    new Set(carrierRecipients.flatMap((r) => r.carrierNames ?? [])),
                  );
                  return (
                    <div key={carrierName} className="border rounded-lg p-4">
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        {firmId ? (
                          <GaMark id={firmId} name={carrierName} />
                        ) : (
                          <Building2 className="h-4 w-4" />
                        )}
                        {carrierName}
                      </h4>
                      {askedFor.length > 0 && (
                        <p className="mb-3 text-xs text-muted-foreground">
                          <span className="font-semibold uppercase tracking-wider">
                            Asked to quote
                          </span>
                          <br />
                          {askedFor.join(", ")}
                        </p>
                      )}
                      <div className="space-y-2">
                        {carrierRecipients.map((recipient, idx) => (
                          <div key={idx} className="flex items-center justify-between py-2 border-b last:border-b-0">
                            <div className="flex items-center gap-3">
                              <Mail className="h-4 w-4 text-muted-foreground" />
                              <div>
                                <p className="font-medium">{recipient.name || recipient.email}</p>
                                <p className="text-sm text-muted-foreground">{recipient.email}</p>
                              </div>
                            </div>
                            {/* Guarded: RFPs sent before the send endpoint
                                recorded coverageTypes have neither this nor
                                name, and an unguarded map threw on the whole
                                card. */}
                            <div className="flex flex-wrap gap-1">
                              {(recipient.coverageTypes ?? []).map((ct: string) => (
                                <Badge key={ct} variant="outline" className="text-xs">
                                  {formatCoverageLabel(ct)}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : Object.keys(selectedCarriers).length > 0 ? (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground mb-4">
                  Detailed recipient information is not available for this RFP. Carriers were selected for the following coverage types:
                </p>
                <div className="grid grid-cols-2 gap-4">
                  {coverageTypes.map((type: string) => {
                    const carrierIds = selectedCarriers[type] || [];
                    return (
                      <div key={type} className="border rounded-lg p-3">
                        <p className="font-medium capitalize">{formatCoverageLabel(type)}</p>
                        <p className="text-sm text-muted-foreground">
                          {carrierIds.length} carrier{carrierIds.length !== 1 ? "s" : ""} selected
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-4">
                No recipient information available
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              RFP Parameters
            </CardTitle>
            <CardDescription>
              Detailed requirements for each coverage type
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {coverageTypes.map((type: string) => {
                const params = rfpParameters[type];
                const Icon = getCoverageIcon(type);
                
                return (
                  <div key={type} className="border rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-4">
                      <div className={`p-2 rounded-lg ${getCoverageColor(type)}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <h4 className="font-semibold text-lg">{formatCoverageLabel(type)}</h4>
                    </div>
                    {params ? (
                      <RFPParametersDisplay type={type} params={params} />
                    ) : (
                      <p className="text-muted-foreground">No parameters specified</p>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
